/* BackendHealthGate — wraps the app and shows a friendly splash whenever
 * /api/health is unreachable (paused/sleeping preview pod, network failure,
 * or transient backend outage). On mount it pings /api/health with a 5s
 * timeout. If unreachable, renders the splash and silently retries every
 * 10 seconds. As soon as a probe succeeds, the children render normally.
 *
 * This is the user-facing fix for the "white screen on paused pod" issue:
 * cached HTML + paused backend = blank page → now shows a clear message
 * and a Refresh button.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCcw, ExternalLink } from "lucide-react";

const HEALTH_PATH = "/api/health";
const TIMEOUT_MS = 5000;
const RETRY_MS = 10000;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

async function probeHealth(signal) {
  const url = `${BACKEND_URL}${HEALTH_PATH}?t=${Date.now()}`;
  const resp = await fetch(url, {
    method: "GET",
    cache: "no-store",
    credentials: "omit",
    signal,
  });
  if (!resp.ok) {
    throw new Error(`health returned ${resp.status}`);
  }
  const data = await resp.json();
  if (!data || data.ok !== true) {
    throw new Error("health body missing ok=true");
  }
  return true;
}

export default function BackendHealthGate({ children }) {
  // `state` is one of: 'checking' (initial probe), 'ok', 'down'
  const [state, setState] = useState("checking");
  const [retryCount, setRetryCount] = useState(0);

  const runProbe = useCallback(async () => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), TIMEOUT_MS);
    try {
      await probeHealth(ac.signal);
      setState("ok");
    } catch (e) {
      setState((prev) => (prev === "ok" ? "ok" : "down"));
    } finally {
      clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    runProbe();
  }, [runProbe]);

  useEffect(() => {
    if (state !== "down") return undefined;
    const id = setInterval(() => {
      setRetryCount((c) => c + 1);
      runProbe();
    }, RETRY_MS);
    return () => clearInterval(id);
  }, [state, runProbe]);

  if (state === "ok") return children;

  // Initial 5-second probe — keep splash minimal so it doesn't flash on fast
  // returns.
  if (state === "checking") {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-slate-50"
        data-testid="backend-health-gate-checking"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  // Backend unreachable splash
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-slate-50 px-4"
      data-testid="backend-health-gate-down"
    >
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-700">
          <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          Preview environment is waking up
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900 font-display leading-tight">
          We can't reach the backend right now.
        </h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          The preview pod for this CRM may have paused after idle. We're retrying
          every 10 seconds — usually it wakes up within a minute. You can also
          tap <strong>Refresh</strong> below to force a reload.
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-2 rounded-md min-h-[44px] px-4 text-sm font-medium text-white"
            style={{ background: "hsl(var(--brand, 348 75% 41%))" }}
            data-testid="health-refresh-button"
          >
            <RefreshCcw className="h-4 w-4" /> Refresh now
          </button>
          <a
            href="https://app.emergent.sh"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-md min-h-[44px] px-4 text-sm font-medium text-slate-700 border border-slate-200 bg-white hover:bg-slate-50"
            data-testid="health-emergent-link"
          >
            Open Emergent <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        <div className="mt-4 text-xs text-slate-500 tabular-nums">
          Retry attempts: {retryCount}
        </div>
      </div>
    </div>
  );
}
